import { z } from 'zod';

/**
 * Order messages sent to customers over WhatsApp (MSG91 templates).
 *
 * The code decides WHEN a message goes out (the event) and which facts are
 * available (the variables). Staff decide WHICH approved MSG91 template is used
 * and in WHAT ORDER the variables fill its {{1}}, {{2}}... slots, so a new or
 * reworded template never needs a deploy. A slot can also hold fixed text that staff type
 * in (stored as "text:<words>"), e.g. the store name.
 */

export const NotificationEvent = {
  LOGIN_OTP: 'LOGIN_OTP',
  ORDER_PLACED: 'ORDER_PLACED',
  ORDER_SHIPPED: 'ORDER_SHIPPED',
  ORDER_DELIVERED: 'ORDER_DELIVERED',
  ORDER_CANCELLED: 'ORDER_CANCELLED',
  ORDER_REFUNDED: 'ORDER_REFUNDED',
  CHECKOUT_REMINDER: 'CHECKOUT_REMINDER',
} as const;
export type NotificationEvent = (typeof NotificationEvent)[keyof typeof NotificationEvent];

export const NotificationVariable = {
  otp_code: 'otp_code',
  otp_minutes: 'otp_minutes',
  store_name: 'store_name',
  customer_name: 'customer_name',
  order_number: 'order_number',
  order_total: 'order_total',
  item_count: 'item_count',
  order_url: 'order_url',
  payment_method: 'payment_method',
  carrier: 'carrier',
  tracking_number: 'tracking_number',
  refund_amount: 'refund_amount',
  checkout_url: 'checkout_url',
} as const;
export type NotificationVariable = (typeof NotificationVariable)[keyof typeof NotificationVariable];

export interface NotificationVariableInfo {
  key: NotificationVariable;
  label: string;
  example: string;
}

export const NOTIFICATION_VARIABLES: Record<NotificationVariable, NotificationVariableInfo> = {
  otp_code: { key: 'otp_code', label: 'The login code', example: '123456' },
  otp_minutes: { key: 'otp_minutes', label: 'Minutes the code is valid', example: '10' },
  store_name: { key: 'store_name', label: 'Store name (MeshSmith)', example: 'MeshSmith' },
  customer_name: { key: 'customer_name', label: 'Customer name', example: 'Asha' },
  order_number: { key: 'order_number', label: 'Order number', example: 'MS-ABC12345' },
  order_total: { key: 'order_total', label: 'Order total', example: '₹1,299.00' },
  item_count: { key: 'item_count', label: 'Number of items', example: '3' },
  order_url: { key: 'order_url', label: 'Link to the order', example: 'https://meshsmith.in/account/orders/MS-ABC12345' },
  payment_method: { key: 'payment_method', label: 'Payment (Paid online / Cash on delivery)', example: 'Paid online' },
  carrier: { key: 'carrier', label: 'Courier', example: 'Delhivery' },
  tracking_number: { key: 'tracking_number', label: 'Tracking number', example: 'DL123456789' },
  refund_amount: { key: 'refund_amount', label: 'Refund amount', example: '₹499.00' },
  checkout_url: { key: 'checkout_url', label: 'Link back to the checkout', example: 'https://meshsmith.in/checkout' },
};

export interface NotificationEventInfo {
  event: NotificationEvent;
  label: string;
  /** When it is sent, in the words shown to staff. */
  when: string;
  variables: NotificationVariable[];
}

export const NOTIFICATION_EVENTS: NotificationEventInfo[] = [
  {
    event: 'LOGIN_OTP',
    label: 'Login code',
    when: 'A customer asks to sign in with their mobile number. Use your approved authentication template. While this is off, the older MSG91_WHATSAPP_TEMPLATE_* settings on the server are used.',
    variables: ['otp_code', 'otp_minutes', 'store_name'],
  },
  {
    event: 'ORDER_PLACED',
    label: 'Order placed',
    when: 'Cash-on-delivery order is placed, or an online payment is confirmed.',
    variables: ['customer_name', 'order_number', 'order_total', 'item_count', 'payment_method', 'order_url', 'store_name'],
  },
  {
    event: 'ORDER_SHIPPED',
    label: 'Order shipped',
    when: 'A shipment is created for the order (one message per shipment).',
    variables: ['customer_name', 'order_number', 'carrier', 'tracking_number', 'order_url', 'store_name'],
  },
  {
    event: 'ORDER_DELIVERED',
    label: 'Order delivered',
    when: 'The order status is set to Delivered.',
    variables: ['customer_name', 'order_number', 'order_url', 'store_name'],
  },
  {
    event: 'ORDER_CANCELLED',
    label: 'Order cancelled',
    when: 'The order status is set to Cancelled.',
    variables: ['customer_name', 'order_number', 'order_total', 'store_name'],
  },
  {
    event: 'ORDER_REFUNDED',
    label: 'Refund issued',
    when: 'A refund is recorded for the order.',
    variables: ['customer_name', 'order_number', 'refund_amount', 'store_name'],
  },
  {
    event: 'CHECKOUT_REMINDER',
    label: 'Left at checkout',
    when: 'A customer opened checkout but did not finish, or cancelled the payment. Sent after the wait set in Admin > Checkout, up to the number of reminders set there.',
    variables: ['customer_name', 'order_total', 'item_count', 'checkout_url', 'store_name'],
  },
];

export function notificationEventInfo(event: string): NotificationEventInfo | undefined {
  return NOTIFICATION_EVENTS.find((e) => e.event === event);
}

const eventEnum = z.enum(['LOGIN_OTP', 'ORDER_PLACED', 'ORDER_SHIPPED', 'ORDER_DELIVERED', 'ORDER_CANCELLED', 'ORDER_REFUNDED', 'CHECKOUT_REMINDER']);

/**
 * What staff edit for one event. `variables` fills the template's body
 * placeholders in order: the first key becomes {{1}}, the second {{2}}, and so on.
 */
export const notificationTemplateSchema = z.object({
  enabled: z.boolean(),
  templateName: z
    .string()
    .trim()
    .max(120)
    .regex(/^[a-z0-9_]*$/, 'Template names use lowercase letters, numbers and underscores only'),
  language: z
    .string()
    .trim()
    .regex(/^[a-z]{2,3}(_[A-Za-z]{2})?$/, 'Use a language code like en or en_US'),
  variables: z.array(z.string().max(220)).max(10),
  /** Which value goes into the template's button (copy-code or dynamic-link button); null for templates without one. */
  buttonVariable: z.string().max(60).nullable().default(null),
  /** Which connected WhatsApp number sends it (digits, country code first, no "+"); null = the default (first) number. */
  senderNumber: z
    .string()
    .trim()
    .regex(/^[1-9]\d{7,14}$/, 'Pick one of the connected WhatsApp numbers')
    .nullable()
    .default(null),
});
export type NotificationTemplateInput = z.infer<typeof notificationTemplateSchema>;

export const FIXED_TEXT_PREFIX = 'text:';
export const isFixedText = (entry: string) => entry.startsWith(FIXED_TEXT_PREFIX);
export const fixedTextOf = (entry: string) => entry.slice(FIXED_TEXT_PREFIX.length).trim();

/** Checks the variables belong to the event and that an enabled template is complete. */
export function validateNotificationTemplate(event: NotificationEvent, input: NotificationTemplateInput): string | null {
  const info = notificationEventInfo(event);
  if (!info) return 'Unknown message type';
  const bad = input.variables.find((v) => !isFixedText(v) && !info.variables.includes(v as NotificationVariable));
  if (bad) return `"${bad}" is not available for ${info.label.toLowerCase()} messages`;
  if (input.variables.some((v) => isFixedText(v) && !fixedTextOf(v))) return 'Fixed text cannot be empty';
  if (input.buttonVariable && !info.variables.includes(input.buttonVariable as NotificationVariable)) {
    return `"${input.buttonVariable}" cannot be used for the button of ${info.label.toLowerCase()} messages`;
  }
  if (input.enabled && !input.templateName) return 'Enter the MSG91 template name before turning this message on';
  return null;
}

export const notificationEventParamSchema = z.object({ event: eventEnum });

export const notificationTestSchema = z.object({
  phone: z.string().trim().min(5).max(20),
});

export interface NotificationTemplateDTO {
  event: NotificationEvent;
  label: string;
  when: string;
  availableVariables: NotificationVariableInfo[];
  enabled: boolean;
  templateName: string;
  language: string;
  /** Variable keys, or "text:<words>" for fixed text, in template slot order. */
  variables: string[];
  buttonVariable: string | null;
  /** The number chosen for this message, or null for the default. */
  senderNumber: string | null;
  updatedAt: string | null;
}

export interface NotificationSettingsDTO {
  /** false = MSG91 is not configured, so nothing can be delivered yet. */
  deliveryConfigured: boolean;
  /** "console" in development: messages are printed in the API log instead of being sent. */
  provider: 'msg91' | 'console';
  /** The WhatsApp numbers connected on the server (digits, no "+"); the first is the default. Empty = none set up. */
  numbers: string[];
  templates: NotificationTemplateDTO[];
}

export interface NotificationLogDTO {
  id: string;
  event: NotificationEvent;
  orderNumber: string | null;
  recipient: string;
  templateName: string;
  status: 'SENT' | 'FAILED' | 'SKIPPED' | 'PENDING';
  /** Which way it went: a WhatsApp message or an email (then `recipient` is the address and `templateName` the email). */
  channel: 'whatsapp' | 'email';
  /** the subject line, for an email */
  subject: string | null;
  /** The WhatsApp number it was sent from (digits, no "+"), when known. */
  senderNumber: string | null;
  error: string | null;
  createdAt: string;
}
